import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { IoInformationCircleOutline } from 'react-icons/io5';

interface InfoTooltipProps {
   text?: string;
}

export function InfoTooltip({ text }: InfoTooltipProps) {
   const [isVisible, setIsVisible] = React.useState(false);
   const [position, setPosition] = React.useState<{ top: number, left: number } | null>(null);
   const iconRef = React.useRef<HTMLSpanElement>(null);

   // Rendered through a portal into <body>, positioned from the icon's real screen
   // coordinates — so the bubble floats above any ancestor with overflow:hidden/auto
   // (node panels, modals, etc.) instead of being clipped by it.
   const updatePosition = React.useCallback(() => {
      if (!iconRef.current) return;
      const rect = iconRef.current.getBoundingClientRect();
      setPosition({ top: rect.top - 8, left: rect.left + rect.width / 2 });
   }, []);

   const handleEnter = () => { updatePosition(); setIsVisible(true); };
   const handleLeave = () => setIsVisible(false);

   React.useEffect(() => {
      if (!isVisible) return;
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
         window.removeEventListener('scroll', updatePosition, true);
         window.removeEventListener('resize', updatePosition);
      };
   }, [isVisible, updatePosition]);

   if (!text) return null;

   return (<>
      <Icon
         ref={ iconRef }
         className="infoTooltip"
         onMouseDown={(e) => e.stopPropagation()}
         onMouseEnter={ handleEnter }
         onMouseLeave={ handleLeave }
      >
         <IoInformationCircleOutline size={14} />
      </Icon>
      { isVisible && position ? createPortal(
         <Bubble style={{ top: position.top, left: position.left }}>
            { text }
         </Bubble>,
         document.body
      ) : null }
   </>);
}

const Icon = styled.span`
   display: inline-flex;
   align-items: center;
   margin-left: 5px;
   cursor: help;
   color: #9ca3af;
   vertical-align: middle;

   &:hover { color: #6b7280; }
`;

// Fixed + portaled: positioned in viewport coordinates, not relative to any
// scrolling/clipping ancestor. z-index matches the app's floating dropdown menus.
const Bubble = styled.div`
   position: fixed;
   z-index: 40000001;
   transform: translate(-50%, -100%);
   background: #111827;
   color: #fff;
   font-size: 12px;
   line-height: 1.45;
   font-weight: 400;
   padding: 8px 10px;
   border-radius: 6px;
   width: max-content;
   max-width: 230px;
   white-space: pre-line;
   pointer-events: none;
   box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.15);

   &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: #111827;
   }
`;
